import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ExerciseCard } from "./components/ExerciseCard";
import { Exercise } from "../exercise.interfaces";
import { useExerciseContext } from "@/pages/Admin/Modules/Edit/exercises/context";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState, useEffect } from "react";

interface ExerciseListProps {
  exercises: Exercise[];
  onAdd?: () => void;
  onEdit?: (exerciseId: string | number) => void;
  onDelete?: (exerciseId: string | number) => void;
  onExerciseClick?: (exerciseId: string | number) => void;
  onExerciseActiveChange?: (exerciseId: string | number, isActive: boolean) => void;
  context: "level-test" | "module" | "levelTestGroup";
  onOrderChange?: (newOrder: (string | number)[]) => void;
  openDialog?: (exercise?: any) => void;
  batchUpdateExercisesSortOrder?: (newOrder: (string | number)[]) => Promise<boolean>;
}

// Props para o componente SortableExerciseCard
interface SortableExerciseCardProps {
  exercise: Exercise;
  onDelete?: () => void;
  onClick: () => void;
  onActiveChange?: (isActive: boolean) => void;
}

// Novo componente para tornar o ExerciseCard "arrastável"
function SortableExerciseCard({
  exercise,
  ...props
}: SortableExerciseCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: exercise.id as string | number,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ExerciseCard exercise={exercise} {...props} />
    </div>
  );
}

export const ExerciseList: React.FC<ExerciseListProps> = ({
  exercises,
  onAdd,
  onEdit,
  onDelete,
  onExerciseClick,
  onExerciseActiveChange,
  context,
  onOrderChange,
  openDialog: propOpenDialog,
  batchUpdateExercisesSortOrder: propBatchUpdateExercisesSortOrder
}) => {
  // Estado local para a ordem dos exercícios
  const [items, setItems] = useState<(string | number)[]>([]);
  
  // Use props if provided, otherwise fall back to context
  let contextMethods = { openDialog: () => {}, batchUpdateExercisesSortOrder: async () => false };
  
  try {
    if (!propOpenDialog || !propBatchUpdateExercisesSortOrder) {
      const context = useExerciseContext();
      contextMethods = context;
    }
  } catch (error) {
    // Context not available, use props or defaults
  }
  
  const openDialog = propOpenDialog || contextMethods.openDialog;
  const batchUpdateExercisesSortOrder = propBatchUpdateExercisesSortOrder || contextMethods.batchUpdateExercisesSortOrder;

  // Inicializa os itens quando os exercícios mudam
  useEffect(() => {
    const validIds = exercises
      .map((e) => e.id)
      .filter((id): id is string | number => id !== undefined);

    setItems(validIds);
  }, [exercises]);

  // Atualiza a ordem localmente ao arrastar
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.indexOf(active.id as string | number);
      const newIndex = items.indexOf(over?.id as string | number);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        // Atualizar a ordem usando o método do contexto - única chamada para o backend
        batchUpdateExercisesSortOrder(newItems);
        
        // Também chamar o callback onOrderChange se existir
        if (onOrderChange) {
          onOrderChange(newItems);
        }
      }
    }
  };

  // Sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleAdd = () => {
    if (onAdd) {
      onAdd();
    }
    openDialog();
  };

  const handleExerciseClick = (exercise: Exercise) => {
    // Se o ID não existe, não faz nada
    if (typeof exercise.id === "undefined") {
      return;
    }

    // Se tiver um callback onExerciseClick, usa ele
    if (onExerciseClick) {
      onExerciseClick(exercise.id as string | number);
    }
    // Se tiver um callback onEdit, usa ele
    else if (onEdit) {
      onEdit(exercise.id as string | number);
    }
    // Se não tiver nenhum callback, apenas abre o diálogo
    else {
      openDialog(exercise);
    }
  };

  // Função para lidar com exclusão, garantindo que o ID é válido
  const handleDelete = (exercise: Exercise) => {
    if (onDelete && typeof exercise.id !== "undefined") {
      onDelete(exercise.id as string | number);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">
          {context === "level-test" || context === "levelTestGroup"
            ? "Exercícios do Teste"
            : "Exercícios do Módulo"}
        </h2>
        <Button
          onClick={handleAdd}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Adicionar Exercício
        </Button>
      </div>

      {exercises.length === 0 ? (
        <div className="text-center py-8 text-neutral-500 bg-neutral-50 rounded-lg border border-neutral-200">
          Nenhum exercício ainda. Clique em "Adicionar Exercício" para criar um.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {items.map((id) => {
                const exercise = exercises.find((e) => e.id === id);
                if (!exercise) return null;

                return (
                  <SortableExerciseCard
                    key={id}
                    exercise={exercise}
                    onDelete={
                      onDelete && typeof exercise.id !== "undefined"
                        ? () => handleDelete(exercise)
                        : undefined
                    }
                    onClick={() => handleExerciseClick(exercise)}
                    onActiveChange={
                      context === "module" && onExerciseActiveChange
                        ? (isActive) => onExerciseActiveChange(id, isActive)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
